import { BaseService } from './BaseService';
import { Report } from '../models';
import { createLogger } from './Debug';
// Report Service
export class ReportService extends BaseService {
  constructor() {
    super();
  }

  // Report Generation
  async generateReport(reportType, transcription, annex = null, language = null) {
    // If transcription is empty or very short but we have an annex, append instruction to use the annex
    const reportTypeName = (typeof reportType === 'string' ? reportType : reportType?.name) || 'rapport';
    let effectiveTranscription = transcription || '';
    const hasAnnex = annex !== null && String(annex).trim().length > 0;
    if (String(effectiveTranscription).trim().length < 10 && hasAnnex) {
      const prefix = effectiveTranscription && String(effectiveTranscription).trim().length > 0 ? effectiveTranscription + '\n\n' : '';
      effectiveTranscription = `${prefix}Génère le ${reportTypeName} à partir de l'annexe`;
    }

    const payload = {
      reportType: reportTypeName,
      transcription: effectiveTranscription
    };
    
    

    if (reportType && typeof reportType === 'object' && typeof reportType.isEnterprise === 'boolean') {
      payload.isEnterprise = reportType.isEnterprise;
      
    }
    
    if (annex !== null) {
      payload.annex = annex;
      
    }
    
    if (language !== null) {
      payload.language = language;
      
    }
    
    const response = await this.post('/reports/generate', payload);
    
    return new Report(response.report);
  }

  async uploadVoice(audioFile) {
    const formData = new FormData();
    formData.append('voice', audioFile);
  const data = await this.postFormData('/reports/upload-voice', formData);
  return data;
  }

  // Méthodes mises à jour dans ReportService

async transcribeAudio(audioFile, options = {}) {
  
  const formData = new FormData();
  formData.append('voice', audioFile);
  
  // Optionally include report type and processed text context
  if (options.reportType) {
    formData.append('reportType', options.reportType);
  }
  if (options.processedText) {
    formData.append('processedText', options.processedText);
  }
  if (typeof options.durationSeconds === 'number' && Number.isFinite(options.durationSeconds)) {
    formData.append('durationSeconds', Number(options.durationSeconds));
  }  
  
  try {
    const data = await this.postFormData('/reports/transcribe', formData);
    
    return data.data.transcription;
    
  } catch (error) {
    const log = createLogger('ReportService.transcribeAudio');
    log.error('❌ [ReportService] Transcription failed:', error);
    throw error;
  }
}

  // Report Management
  async getReports(filters = {}) {
    const params = new URLSearchParams();
    if (filters.reportType) params.append('reportType', filters.reportType);
    if (filters.isArchived !== undefined) params.append('isArchived', filters.isArchived);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    const response = await this.get(`/reports?${params}`);
    return response.reports.map(r => new Report(r));
  }

  async getReport(id) {
    const response = await this.get(`/reports/${id}`);
    return new Report(response.report);
  }

  async updateReport(id, updates) {
    
    const response = await this.put(`/reports/${id}`, updates);
    return new Report(response.report);
  }

  async deleteReport(id) {
    await this.put(`/reports/${id}/delete`);
  }

  async archiveReport(id) {
    await this.put(`/reports/${id}/archive`);
  }

  async markReportAsRead(id) {
    await this.put(`/reports/${id}/read`);
  }

  // Advanced search
  async searchReports({ q = '', qTerms = [], qOperator = '', startDate = '', endDate = '', reportType = '', includeEnterprise = false, generatedBy = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (Array.isArray(qTerms) && qTerms.length > 0) {
      if (qOperator) params.append('qOperator', qOperator);
      if (String(qOperator).toUpperCase() === 'OR') {
        params.append('qTerms', qTerms.filter(Boolean).join(','));
      } else {
        qTerms.filter(Boolean).forEach((term) => params.append('qTerms', term));
      }
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (reportType) params.append('reportType', reportType);
    if (includeEnterprise) params.append('includeEnterprise', 'true');
    if (generatedBy) params.append('generatedBy', generatedBy);
    if (limit !== undefined && limit !== null) params.append('limit', String(limit));
    if (offset !== undefined && offset !== null) params.append('offset', String(offset));
    const response = await this.get(`/reports/search?${params.toString()}`);
    const list = response.data.reports;
    
    return list.map(r => new Report(r));
  }

  // Live dictations - fetch without persisting
  async getLiveReports() {
    const response = await this.get('/reports/live');
    const list = (response && response.data && (response.data.reports || response.data.list))
      || response?.reports
      || response?.list
      || [];
    return Array.isArray(list) ? list.map((r) => new Report(r)) : [];
  }

  // Sync
  async getInitialReports() {
    const response = await this.get('/reports/initial');
    return {
      reports: response.reports.map(r => new Report(r)),
      stats: response.stats,
      syncDate: new Date(response.syncDate)
    };
  }

  async refreshReports(lastSyncDate) {
    const response = await this.post('/reports/refresh', { 
      lastSyncDate: lastSyncDate.toISOString() 
    });
    
    return {
      reports: response.reports.map(r => new Report(r)),
      deletedIds: response.deletedIds,
      stats: response.stats,
      syncDate: new Date(response.syncDate)
    };
  }

  // Send audio directly to live-voice endpoint
  async sendLiveVoice(audioFile, annex = null, options = {}) {
    const formData = new FormData();
    formData.append('voice', audioFile);
    if (annex) {
      formData.append('annex', annex);
    }

    // Add shouldMettreEnForme parameter if provided
    if (options && options.shouldMettreEnForme === true) {
      formData.append('shouldMettreEnForme', 'true');
    }

    if (options && typeof options.durationSeconds === 'number' && Number.isFinite(options.durationSeconds)) {
      formData.append('durationSeconds', String(Number(options.durationSeconds)));
    }
    const response = await this.postFormData('reports/live-voice', formData);
    return response.data;
  }
}
